import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import AuthLayout from '@shared/ui/access-layout';
import { Button } from '@shared/ui/button';

import { authApi } from '../../../api/services/auth.service';

type Status = 'loading' | 'success' | 'error';

const EmailConfirm = () => {
  const { code } = useParams<{ code: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [errorText, setErrorText] = useState<string>('');

  const calledRef = useRef(false);

  useEffect(() => {
    document.title = 'Подтверждение почты';
  }, []);

  useEffect(() => {
    if (!code) {
      setStatus('error');
      setErrorText('Не указан код подтверждения.');
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;

    authApi
      .confirmEmail(code)
      .then(() => {
        setStatus('success');
        setErrorText('');
      })
      .catch((err: any) => {
        setStatus('error');
        const message =
          err?.response?.data?.detail ??
          err?.response?.data ??
          err?.message ??
          'Не удалось подтвердить email. Возможно, ссылка истекла.';
        setErrorText(
          typeof message === 'string'
            ? message
            : 'Не удалось подтвердить email. Возможно, ссылка истекла.'
        );
      });
  }, [code]);

  const retry = () => {
    if (!code) return;
    setStatus('loading');
    calledRef.current = false;
    setTimeout(() => {
      calledRef.current = true;
      authApi
        .confirmEmail(code)
        .then(() => {
          setStatus('success');
          setErrorText('');
        })
        .catch((err: any) => {
          setStatus('error');
          const message =
            err?.response?.data?.detail ??
            err?.response?.data ??
            err?.message ??
            'Не удалось подтвердить email. Возможно, ссылка истекла.';
          setErrorText(
            typeof message === 'string'
              ? message
              : 'Не удалось подтвердить email. Возможно, ссылка истекла.'
          );
        });
    }, 150);
  };

  return (
    <AuthLayout
      leftContent={
        <section className="flex justify-center items-center flex-col absolute top-[45%] left-[25%] -translate-x-1/2 -translate-y-[20%] text-center w-[346px]">
          <h2 className="text-[28px] text-white">Подтверждение почты</h2>
          <div className="my-[34px] mx-auto w-[113px] border border-white" />
        </section>
      }
    >
      <div className="w-full max-w-[70%] relative">
        {status === 'loading' && (
          <>
            <h2 className="font-bold text-[28px] text-[#141414]">
              Подтверждаем email…
            </h2>
            <p className="mt-[45px] text-[#b4b4b4] text-base leading-[140%]">
              Пожалуйста, подождите — мы проверяем ссылку подтверждения.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <h2 className="font-bold text-[28px] text-[#141414]">
              Email подтверждён
            </h2>
            <p className="mt-[45px] text-[#b4b4b4] text-base leading-[140%]">
              Спасибо! Ваш аккаунт активирован. Теперь вы можете войти в
              личный кабинет, используя указанные при регистрации данные.
            </p>
            <div className="mt-[30px]">
              <Link to="/login">
                <Button>Перейти на страницу входа</Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 className="font-bold text-[28px] text-[#141414]">
              Не удалось подтвердить email
            </h2>
            <p className="mt-[45px] text-[#b4b4b4] text-base leading-[140%]">
              {errorText ||
                'Ссылка подтверждения недействительна или срок её действия истёк (48 часов).'}
            </p>
            <p className="mt-[15px] text-[#b4b4b4] text-base leading-[140%]">
              Если вы недавно регистрировались — попробуйте обновить страницу.
              Если ничего не помогает, свяжитесь со своим персональным
              менеджером, чтобы получить новую ссылку.
            </p>
            <div className="mt-[30px] flex flex-col gap-3">
              <Button onClick={retry}>Попробовать снова</Button>
              <Link
                to="/login"
                style={{ color: '#E3282A' }}
                className="text-[14px] underline"
              >
                Вернуться ко входу
              </Link>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default EmailConfirm;
